import { X } from "lucide-react";
import React, { useState } from "react";
import { BanquetSeatingIcon } from "~/icons/BanquetSeatingIcon";
import { BlockSeatingIcon } from "~/icons/BlockSeatingIcon";
import { ClassroomSeatingIcon } from "~/icons/ClassroomSeatingIcon";
import { PartyIcon } from "~/icons/PartyIcon";
import { StandingSeatingIcon } from "~/icons/StandingSeatingIcon";
import { TableSeatingIcon } from "~/icons/TableSeatingIcon";
import { TheatreSeatingIcon } from "~/icons/TheatreSeatingIcon";
import { UFormSeatingIcon } from "~/icons/UFormSeatingIcon";
import { XIcon } from "~/icons/XIcon";
import {
  groupImagesByCategory,
  OutsideAreaWithImages,
} from "~/lib/server-functions/get-locations";
import noImageFound from "~/assets/no-image.svg";
import {
  resolveAssetUrl,
  formatGermanNumberWithoutDecimals,
  convertCmToMeters,
} from "~/lib/utils";
import { Button } from "~/components/ui/button";
import ImageGalleryPopUp from "~/components/PopUp/ImageGalleryPopUp";
import OutsideAreaPopUp from "~/components/PopUp/OutsideAreaPopUp";

export type OutsideAreaLayouts = {
  party: string | number | null;
  row: string | number | null;
  parliament: string | number | null;
  block: string | number | null;
  uform: string | number | null;
  banquet: string | number | null;
  table: string | number | null;
  standing: string | number | null;
};

type OutsideAreaLayoutKey = keyof OutsideAreaLayouts;
const layoutsConfig: {
  icon: React.ReactNode;
  valueKey: OutsideAreaLayoutKey;
  label: string;
}[] = [
    { icon: <PartyIcon />, valueKey: "party", label: "Party" },
    { icon: <TheatreSeatingIcon />, valueKey: "row", label: "Reihe" },
    {
      icon: <ClassroomSeatingIcon />,
      valueKey: "parliament",
      label: "Parlament",
    },
    { icon: <BlockSeatingIcon />, valueKey: "block", label: "Block" },
    {
      icon: <UFormSeatingIcon />,
      valueKey: "uform",
      label: "U-Form",
    },
    {
      icon: <BanquetSeatingIcon />,
      valueKey: "banquet",
      label: "Bankett",
    },
    { icon: <TableSeatingIcon />, valueKey: "table", label: "Tafel" },
    { icon: <StandingSeatingIcon />, valueKey: "standing", label: "Stehempfang" },
  ];

const sections = [{ id: "images", label: "Bilder" }];

interface OutsideAreaCardProps {
  outsideArea: OutsideAreaWithImages;
}

export function OutsideAreaCard({ outsideArea }: OutsideAreaCardProps) {
  const layouts: OutsideAreaLayouts = {
    party: outsideArea.seatingParty || null,
    row: outsideArea.seatingRow || null,
    parliament: outsideArea.seatingParliamentary || null,
    block: outsideArea.seatingBlock || null,
    uform: outsideArea.seatingUShape || null,
    banquet: outsideArea.seatingBanquet || null,
    table: outsideArea.seatingTable || null,
    standing: outsideArea.seatingStanding || null,
  };

  const [showDetails, setShowDetails] = useState(false);
  const [activeDetailTab, setActiveDetailTab] = useState(sections[0].id);
  const [activeImageId, setActiveImageId] = useState<undefined | string>();
  const [showGallery, setShowGallery] = useState(false);

  const groupedImages = groupImagesByCategory(outsideArea?.images ?? []);

  const flatGroupImages = Object.values(groupedImages).flatMap((g) => {
    return g.images.map((img) => ({
      ...img,
      path: img.path as string,
      categoryId: g.categoryId,
      category: g.name,
    }));
  });

  const areaName = outsideArea.name?.german || outsideArea.name?.english || "";

  return (
    <div>
      <OutsideAreaPopUp
        open={showDetails}
        images={flatGroupImages}
        outsideArea={outsideArea}
        onOpenChange={(value) => {
          setShowDetails(value);
        }}
        onOpenGallery={(value) => {
          setShowGallery(value);
        }}
      />
      <div
        key={outsideArea.id}
        className="flex flex-col gap-4 py-6 last:pb-0 md:flex-row md:items-center"
      >
        <div className="flex gap-4 md:mr-[100px] basis-[260px] shrink-0">
          {/* Image */}
          <img
            src={
              outsideArea.images[0]?.path
                ? resolveAssetUrl(outsideArea.images[0]!.path!)
                : noImageFound
            }
            alt={areaName}
            className="h-[107px] w-[115px] shrink-0 rounded-lg object-cover"
          />

          {/* Infos */}
          <div className="flex flex-col">
            <div className="font-semibold">{areaName}</div>
            {outsideArea.maxArea != null && outsideArea.maxArea > 0 && (
              <div className="text-sm">
                {formatGermanNumberWithoutDecimals(
                  convertCmToMeters(outsideArea.maxArea),
                )}{" "}
                m²
              </div>
            )}
            <Button
              variant="link"
              className="mt-1 p-0 inline-block text-sm font-medium text-dove-gray hover:underline w-fit"
              onClick={() => setShowDetails(!showDetails)}
            >
              Mehr Infos
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-start justify-start gap-4 md:mt-0 md:flex-nowrap">
          {/* Layouts */}
          {Object.entries(layouts).map(([key, value]) => {
            const item = layoutsConfig.find((l) => l.valueKey === key);
            if (!item) return null;

            return (
              <div
                key={key}
                className="flex flex-col items-center justify-center"
              >
                <div className="mb-2">{item.label}</div>
                <div className="rounded-md">{item.icon}</div>
                <span className="mt-2">{value ?? <XIcon />}</span>
              </div>
            );
          })}
        </div>
      </div>
      {showDetails && (
        <div className="mt-4 border-t">
          <div className="w-full bg-white z-40 border-b-2 layout-place-full-width layout-content-grid mb-4 flex justify-center relative">
            <div className="flex gap-5 py-4 relative w-fit">
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveDetailTab(s.id)}
                  className="relative group cursor-pointer px-2"
                >
                  {s.label}
                  {activeDetailTab === s.id ? (
                    <span className="absolute -bottom-[19px] left-0 w-full h-1 bg-dove-gray rounded-full"></span>
                  ) : (
                    <span className="absolute -bottom-[19px] left-0 w-full h-1 bg-dove-gray rounded-full scale-x-0 group-hover:scale-x-100"></span>
                  )}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowDetails(false)}
              className="absolute right-8 top-1/2 -translate-y-1/2 text-dove-gray hover:text-[#060606] hover:cursor-pointer"
            >
              <X />
            </button>
          </div>
          {activeDetailTab === "images" && (
            <>
              {outsideArea.images && outsideArea.images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {outsideArea.images.map((img, index) => {
                    const isFifthOrSixth = index % 6 === 4 || index % 6 === 5;
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          setActiveImageId(
                            img.id ? img.id.toString() : undefined
                          );
                          setShowGallery(true);
                        }}
                        className={`relative w-full h-[170px] max-h-[170px] overflow-hidden rounded-sm cursor-pointer ${isFifthOrSixth ? "col-span-2" : ""
                          }`}
                      >
                        <img
                          src={
                            img.path ? resolveAssetUrl(img.path) : noImageFound
                          }
                          alt={img.alt || ""}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {showGallery && (
        <ImageGalleryPopUp
          open={showGallery}
          onOpenChange={(val) => setShowGallery(val)}
          images={flatGroupImages}
          activeImageId={activeImageId}
          sections={[
            { id: -1, label: "Alle Bilder" },
            ...Object.values(groupedImages)
              .filter((g) => g.categoryId !== null && g.categoryId !== -1)
              .map((g) => ({
                id: g.categoryId!,
                label: g.name!,
              })),
          ]}
        />
      )}
    </div>
  );
}
